import React, { useEffect, useState } from 'react';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Card, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import UserPermissionModal from './UserPermissionModal';
import Paginations from '../pagination/Paginations';
import { useSelector } from 'react-redux';
import { allSubAdminApi, blockUnblockSubAdmin, createNewSubAdmin, deleteSubAdmin, subAdminByIdApi, updateSubAdmin } from '../../api';
import { toast } from 'react-toastify';
import { useConfirm } from '../confirmDialog/useConfirm';
import EditUserPermission from './EditUserPermission';
import { AdminTable, ActionCell } from '../adminTable';

const getStatusLabel = (status) => {
  switch (status) {
    case 1: return 'Unverified';
    case 2: return 'Active';
    case 3: return 'Blocked';
    case 4: return 'Deleted';
    case 5: return 'Rejected';
    default: return '-';
  }
};

const UserPermission = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [admin, setAdmin] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [change, setChange] = useState(false);

  const allAdmin = async () => {
    try {
      setLoading(true);
      const response = await allSubAdminApi({ token, currentPage });
      setAdmin(response?.data || []);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching sub-admins:", error);
    }
  };

  const handleBlockResume = async (id, currentLockStatus) => {
    const newLockStatus = !currentLockStatus;
    const action = newLockStatus ? 'block' : 'resume';
    const isConfirmed = await confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Admin`,
      description: `Are you sure you want to ${action} this User/Admin?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
    });
    if (!isConfirmed) return;
    try {
      const response = await blockUnblockSubAdmin({ token, id, lock: newLockStatus });
      toast.success(response?.message);
      setChange(!change);
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const handleDelete = async (id) => {
    const isConfirmed = await confirm({ title: "Delete Admin", description: "Are you sure you want to delete this User/Admin?", confirmText: "Delete", destructive: true });
    if (!isConfirmed) return;
    try {
      const response = await deleteSubAdmin({ token, id });
      toast.success(response?.message);
      setChange(!change);
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const addAdmin = async (newAdminData) => {
    try {
      const response = await createNewSubAdmin({ token, newAdminData });
      toast.success(response?.message);
      setChange(!change);
      setOpen(false);
    } catch (error) {
      toast.error("Error creating new admin");
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await subAdminByIdApi({ token, id });
      setEditingData(response?.data || null);
      setOpen(true);
    } catch (error) {
      toast.error("An error occurred while fetching user data");
    }
  };

  const EditSubAdmin = async (updatedData) => {
    try {
      const response = await updateSubAdmin({ token, id: editingData?._id, editedValue: updatedData });
      toast.success(response?.message || 'Sub-admin updated successfully');
      setChange(!change);
      setOpen(false);
    } catch (error) {
      toast.error(error.message || 'An error occurred during the update');
    }
  };

  useEffect(() => {
    allAdmin();
  }, [currentPage, change]);

  const handleClickOpen = () => {
    setEditingData(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingData(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditingData((prevData) => ({ ...prevData, [name]: value }));
  };

  const columns = [
    { field: "username", headerName: "Name", minWidth: 140, renderCell: (r) => r.username || "-" },
    { field: "email", headerName: "Email", minWidth: 180, renderCell: (r) => r.email || "-" },
    { field: "phone", headerName: "Mobile No.", width: 130, renderCell: (r) => r.phone || "-" },
    { field: "account_status", headerName: "Status", width: 100, renderCell: (r) => getStatusLabel(r.account_status) },
    {
      field: "createdAt",
      headerName: "Created Date",
      width: 130,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (r) => new Date(r.createdAt).toLocaleDateString(),
    },
    {
      field: "__actions",
      headerName: "",
      width: 120,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "Edit",
              icon: <EditIcon fontSize="small" />,
              onClick: () => handleEdit(row._id),
            },
            row.lock
              ? {
                  label: "Unblock",
                  icon: <LockOpenIcon fontSize="small" />,
                  color: "#065F46",
                  onClick: () => handleBlockResume(row._id, row.lock),
                }
              : {
                  label: "Block",
                  icon: <LockIcon fontSize="small" />,
                  color: "#362B24",
                  onClick: () => handleBlockResume(row._id, row.lock),
                },
            {
              label: "Delete",
              icon: <DeleteIcon fontSize="small" />,
              color: "#EA001B",
              onClick: () => handleDelete(row._id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <Box sx={{ marginTop: '15px', paddingLeft: '10px' }}>
        <Box
          onClick={() => navigate('/dashboard')}
          sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer", width: "fit-content" }}
        >
          <ArrowBackIcon sx={{ color: "#065F46" }} />
          <Typography sx={{ color: "#065F46", fontWeight: "500", fontSize: "20px" }}>Back</Typography>
        </Box>
      </Box>

      <Card sx={{ padding: "20px", borderRadius: "25px", marginTop: "30px" }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Typography sx={{ fontSize: '18px', fontWeight: '600' }}>All Users Permissions</Typography>
          <Button
            onClick={handleClickOpen}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: '29px',
              background: 'linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)',
              boxShadow: '1px 2px 30px 0px rgba(0, 0, 0, 0.06)',
              height: '50px',
              color: 'white',
              textTransform: 'none',
              px: 3,
              "&:hover": { opacity: 0.9 },
            }}
          >
            Add New Permission
          </Button>
        </Box>

        <AdminTable
          columns={columns}
          rows={admin}
          loading={loading}
          rowKey="_id"
          emptyText="No admins found"
        />
        <Paginations
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={(page) => { setLoading(true); setCurrentPage(page); }}
          disabled={loading}
        />
      </Card>

      <UserPermissionModal
        open={open}
        onClose={handleClose}
        editingData={editingData}
        onSave={addAdmin}
        handleInputChange={handleInputChange}
      />

      {editingData && (
        <EditUserPermission
          open={open}
          onClose={handleClose}
          editingData={editingData}
          onSave={EditSubAdmin}
        />
      )}
    </div>
  );
};

export default UserPermission;
